import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { AjaxResponse } from 'rxjs/ajax';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { environment } from '../../../../environments/environment';
import { PrintSheetUtils } from './utils';
import { each as _each, map as _map, get as _get } from 'lodash';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BaseRequester } from '../common/base-requester';
import { InvoiceParameter } from './interfaces/invoice-param';
import { RenderRequest } from './interfaces/report-api-request';
import { ShopeeShippingLabelParameter } from './interfaces/shopee-shipping-label-param';
import { CustomTemplateParam } from './interfaces/custom-template-param';
import { QuoteParameter } from './interfaces/quote-param';
import { DeliveryNoteParameter } from './interfaces/delivery-note-param';
import { TaxInvoicePrintSheetParameter } from './interfaces/tax-invoice-param';
import { OutputTaxPrintSheetParameter } from './interfaces/output-tax-request';
import { ReceiptNoteParameter } from './interfaces/receipt-note-param';

// Output format of a rendered document. 'jpg' renders the template through
// jsreport's chrome-image recipe instead of chrome-pdf.
export type RenderedFileFormat = 'pdf' | 'jpg';

@Injectable({
    providedIn: 'root',
})
export class PrintSheetService extends BaseRequester {
    // Pixel size of a receipt JPEG: an A4 sheet at twice Chrome's 96 CSS
    // px/in (A4 is 794 × 1123 CSS px), so the text stays sharp when a chat
    // client scales the image down and the receipt prints well from it. The
    // template scales its sheet to whatever width is sent, so only the
    // aspect ratio has to stay A4.
    private static readonly receiptJpegSize = { width: 1588, height: 2246 };

    printQuote(
        quoteParameters: QuoteParameter[],
        tabName?: string,
    ): Observable<boolean> {
        const parameter: RenderRequest<QuoteParameter> = {
            template: {
                name: environment.quoteTemplateName,
            },
            data: {
                pages: quoteParameters,
            },
        };
        return this.print(parameter, tabName);
    }

    printShippingLabel(
        shippingLabelParams: ShopeeShippingLabelParameter[],
        templateName: string,
        scale: number,
        tabName?: string,
    ): Observable<boolean> {
        return this.print(
            this.buildShippingLabelParameter(
                shippingLabelParams,
                templateName,
                scale,
            ),
            tabName,
        );
    }

    renderShippingLabelBase64(
        shippingLabelParams: ShopeeShippingLabelParameter[],
        templateName: string,
        scale: number,
    ): Observable<string> {
        return this.renderBase64(
            this.buildShippingLabelParameter(
                shippingLabelParams,
                templateName,
                scale,
            ),
        );
    }

    printInvoice(
        invoiceParams: InvoiceParameter[],
        tabName?: string,
    ): Observable<boolean> {
        const parameter: RenderRequest<InvoiceParameter> = {
            template: {
                name: environment.invoiceReportTemplateName,
            },
            data: {
                pages: invoiceParams,
            },
        };
        return this.print(parameter, tabName);
    }

    printTaxInvoice(
        taxInvoiceParams: TaxInvoicePrintSheetParameter[],
        tabName?: string,
    ): Observable<boolean> {
        const parameter: RenderRequest<TaxInvoicePrintSheetParameter> = {
            template: {
                name: environment.taxInvoiceReportTemplateName,
            },
            data: {
                pages: taxInvoiceParams,
            },
        };
        return this.print(parameter, tabName);
    }

    // Download twin of printTaxInvoice: the same render, returned as base64
    // instead of opened in a tab, so callers can merge batches into one file.
    renderTaxInvoiceBase64(
        taxInvoiceParams: TaxInvoicePrintSheetParameter[],
    ): Observable<string> {
        return this.renderBase64({
            template: {
                name: environment.taxInvoiceReportTemplateName,
            },
            data: {
                pages: taxInvoiceParams,
            },
        });
    }

    mergePdfBase64ToBlob(
        base64Pdfs: string[],
    ): Observable<{ blob: Blob; pageCount: number }> {
        return from(this.mergePdfBase64(base64Pdfs)).pipe(
            map((merged) => ({
                blob: PrintSheetUtils.base64ToBlob(
                    merged.base64,
                    'application/pdf',
                ),
                pageCount: merged.pageCount,
            })),
        );
    }

    printDeliveryNote(
        deliveryNoteParams: DeliveryNoteParameter[],
        tabName?: string,
    ): Observable<boolean> {
        const parameter: RenderRequest<DeliveryNoteParameter> = {
            template: {
                name: environment.deliveryNoteTemplateName,
            },
            data: {
                pages: deliveryNoteParams,
            },
        };
        return this.print(parameter, tabName);
    }

    printReceiptNote(
        receiptParams: ReceiptNoteParameter[],
        tabName?: string,
    ): Observable<boolean> {
        return this.print(
            this.buildReceiptNoteRenderRequest(receiptParams, 'pdf'),
            tabName,
        );
    }

    // The receipt as a downloadable file. 'jpg' is for Lazada, whose chat
    // accepts images but neither PDFs nor external links; 'pdf' for everyone
    // else.
    renderReceiptNoteBlob(
        receiptParams: ReceiptNoteParameter[],
        format: RenderedFileFormat,
    ): Observable<Blob> {
        return this.renderBlob(
            this.buildReceiptNoteRenderRequest(receiptParams, format),
            format,
        );
    }

    // Public because the save-and-share path hands the same request to the
    // render-persist Lambda: whatever the download renders is exactly what
    // lands in Drive, format included.
    buildReceiptNoteRenderRequest(
        receiptParams: ReceiptNoteParameter[],
        format: RenderedFileFormat,
    ): RenderRequest<ReceiptNoteParameter> {
        return {
            template: {
                name: environment.receiptNoteTemplateName,
                ...(format === 'jpg'
                    ? {
                          recipe: 'chrome-image' as const,
                          chromeImage: {
                              type: 'jpeg' as const,
                              quality: 92,
                              // The viewport IS the sheet: the template's
                              // screen stylesheet scales its A4 page to the
                              // viewport width and carries the PDF's page
                              // margins, so the image is the PDF page at
                              // this pixel size. fullPage lets a receipt
                              // longer than one sheet run on below it
                              // instead of being cut off; a shorter one
                              // still fills the sheet, as the capture is
                              // never smaller than the viewport.
                              fullPage: true,
                              viewportWidth:
                                  PrintSheetService.receiptJpegSize.width,
                              viewportHeight:
                                  PrintSheetService.receiptJpegSize.height,
                          },
                      }
                    : {}),
            },
            data: {
                pages: receiptParams,
            },
        };
    }

    // Prints a mixed stack of tax-invoice document sets and plain invoices as
    // ONE tab, keeping the caller's entry order so the sheet stack pairs with
    // the shipping-label stack at the pack bench. Emits true when every sheet
    // is in entry order; false when a document spilled onto multiple pages
    // and the stack fell back to tax-then-plain grouping (per-order page
    // attribution is unknowable then — callers should tell the user).
    printOutputTaxReport(
        outputTaxParam: OutputTaxPrintSheetParameter,
        tabName?: string,
    ) {
        const parameter: RenderRequest<OutputTaxPrintSheetParameter> = {
            template: {
                name: environment.outputTaxReportTemplateName,
            },
            data: outputTaxParam,
        };
        return this.print(parameter, tabName);
    }

    printLazadaShippingLabelTemplate(
        htmls: string[],
        tabName: string,
    ): Observable<boolean> {
        return this.print(
            this.buildLazadaShippingLabelParameter(htmls),
            tabName,
        );
    }

    renderLazadaShippingLabelBase64(htmls: string[]): Observable<string> {
        return this.renderBase64(this.buildLazadaShippingLabelParameter(htmls));
    }

    stampTiktokMatchingNumber(
        pdf: string | ArrayBuffer,
        matchingNumber: string,
    ): Observable<string> {
        return from(this.stampMatchingNumber(pdf, matchingNumber));
    }

    openMergedPdfTab(
        base64Pdfs: string[],
        tabName: string,
    ): Observable<boolean> {
        return from(this.mergePdfBase64(base64Pdfs)).pipe(
            map((merged) => {
                this.openNewPdfTabFromBase64(
                    `${tabName} (${merged.pageCount})`,
                    merged.base64,
                );
                return true;
            }),
        );
    }

    private buildShippingLabelParameter(
        shippingLabelParams: ShopeeShippingLabelParameter[],
        templateName: string,
        scale: number,
    ): RenderRequest<ShopeeShippingLabelParameter> {
        return {
            template: {
                name: templateName,
                chrome: {
                    scale,
                },
            },
            data: {
                pages: shippingLabelParams,
            },
        };
    }

    private buildLazadaShippingLabelParameter(
        htmls: string[],
    ): RenderRequest<CustomTemplateParam> {
        return {
            template: {
                name: environment.lazadaShipingLabelRenderTemplateName,
                chrome: {
                    scale: 0.68,
                    width: '410px',
                    height: '570px',
                },
            },
            data: {
                pages: _map(htmls, (html) => {
                    return {
                        template: html,
                    };
                }),
            },
        };
    }

    // Placement of the matching number on TikTok's pre-rendered label PDF,
    // as a ratio of the page size: a bordered box in the top-right corner,
    // on the row with the service-type marker (NDD/EZ), left of the side
    // barcode strip. The box is anchored by its right edge.
    private static readonly tiktokMatchingRightRatio = 0.942;
    private static readonly tiktokMatchingTopRatio = 0.01;
    private static readonly tiktokMatchingFontRatio = 0.033;

    private async stampMatchingNumber(
        pdf: string | ArrayBuffer,
        matchingNumber: string,
    ): Promise<string> {
        const document = await PDFDocument.load(pdf);
        const font = await document.embedFont(StandardFonts.HelveticaBold);
        _each(document.getPages(), (page) => {
            const { width, height } = page.getSize();
            const fontSize = height * PrintSheetService.tiktokMatchingFontRatio;
            const textWidth = font.widthOfTextAtSize(matchingNumber, fontSize);
            const padding = fontSize * 0.3;
            const boxWidth = textWidth + padding * 2;
            const boxHeight = fontSize + padding * 2;
            const boxX =
                width * PrintSheetService.tiktokMatchingRightRatio - boxWidth;
            const boxY =
                height -
                height * PrintSheetService.tiktokMatchingTopRatio -
                boxHeight;
            page.drawRectangle({
                x: boxX,
                y: boxY,
                width: boxWidth,
                height: boxHeight,
                color: rgb(1, 1, 1),
                borderColor: rgb(0, 0, 0),
                borderWidth: 1.5,
            });
            page.drawText(matchingNumber, {
                x: boxX + padding,
                y: boxY + padding + fontSize * 0.1,
                size: fontSize,
                font,
                color: rgb(0, 0, 0),
            });
        });
        return document.saveAsBase64();
    }

    // Rebuilds the two rendered PDFs into one document following the entry
    // order. Interleaving needs a 1:1 entry→page mapping; when a long item
    // list spills a document onto extra pages, per-order attribution is
    // unknowable and the stack degrades to tax-then-plain grouping.
    private async mergePdfBase64(
        base64Pdfs: string[],
    ): Promise<{ base64: string; pageCount: number }> {
        const mergedDocument = await PDFDocument.create();
        for (const base64 of base64Pdfs) {
            const document = await PDFDocument.load(base64);
            const pages = await mergedDocument.copyPages(
                document,
                document.getPageIndices(),
            );
            _each(pages, (page) => mergedDocument.addPage(page));
        }
        return {
            base64: await mergedDocument.saveAsBase64(),
            pageCount: mergedDocument.getPageCount(),
        };
    }

    private print(
        parameter: RenderRequest<any>,
        tabName?: string,
    ): Observable<boolean> {
        return this.renderPdfBlob(parameter).pipe(
            map((pdfBlob: Blob) => {
                const pageCount: number = _get(
                    parameter,
                    'data.pages.length',
                    0,
                );
                const tName = `${tabName || parameter.template.name}${
                    pageCount ? ` (${pageCount})` : ''
                }`;
                this.openNewPdfTabFromBlob(tName, pdfBlob);
                return true;
            }),
        );
    }

    private renderBase64(parameter: RenderRequest<any>): Observable<string> {
        return this.renderPdfBlob(parameter).pipe(
            switchMap((pdfBlob: Blob) =>
                from(PrintSheetUtils.blobToBase64(pdfBlob)),
            ),
        );
    }

    // Posts the render request to the genlogic-jsreport service and
    // resolves to a PDF Blob.
    private renderPdfBlob(parameter: RenderRequest<any>): Observable<Blob> {
        return this.renderBlob(parameter, 'pdf');
    }

    private static readonly MIME_TYPES: Record<RenderedFileFormat, string> = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
    };

    // Magic numbers that identify an already-binary response body:
    // "%PDF" for PDF, the SOI marker + 0xFF for JPEG.
    private static readonly MAGIC_BYTES: Record<RenderedFileFormat, number[]> =
        {
            pdf: [0x25, 0x50, 0x44, 0x46],
            jpg: [0xff, 0xd8, 0xff],
        };

    private renderBlob(
        parameter: RenderRequest<any>,
        format: RenderedFileFormat,
    ): Observable<Blob> {
        return this.internalPostRequest(
            environment.renderReportEndpoint,
            parameter,
            'arraybuffer',
        ).pipe(
            map((response: AjaxResponse) =>
                this.toRenderedBlob(response.response as ArrayBuffer, format),
            ),
            catchError((error: unknown) =>
                throwError(this.toRenderError(error)),
            ),
        );
    }

    // The endpoint may return the document as raw binary, as a base64
    // string, or as a base64 string wrapped in a Lambda-proxy JSON envelope
    // (when API Gateway doesn't decode it). Detect which and produce real
    // bytes of the requested format.
    private toRenderedBlob(
        buffer: ArrayBuffer,
        format: RenderedFileFormat,
    ): Blob {
        const bytes = new Uint8Array(buffer);
        const magic = PrintSheetService.MAGIC_BYTES[format];
        const mimeType = PrintSheetService.MIME_TYPES[format];
        if (
            bytes.length >= magic.length &&
            magic.every((byte, index) => bytes[index] === byte)
        ) {
            return new Blob([bytes], { type: mimeType });
        }
        const text = new TextDecoder().decode(bytes).trim();
        const base64 = text.startsWith('{')
            ? _get(JSON.parse(text), 'body', '')
            : text;
        return PrintSheetUtils.base64ToBlob(
            base64.replace(/\s/g, ''),
            mimeType,
        );
    }

    // The render endpoint reports errors as JSON; with an arraybuffer responseType the
    // error body arrives as bytes, so decode it for a readable message.
    private toRenderError(error: unknown): Error {
        const response: any = _get(error, 'response');
        if (response instanceof ArrayBuffer) {
            const message = this.decodeRenderErrorMessage(response);
            if (message) {
                return new Error(message);
            }
        }
        return error instanceof Error ? error : new Error('Render failed');
    }

    private decodeRenderErrorMessage(buffer: ArrayBuffer): string {
        try {
            const detail = JSON.parse(new TextDecoder().decode(buffer));
            return _get(detail, 'detail') || _get(detail, 'error') || '';
        } catch {
            return '';
        }
    }

    private openNewPdfTabFromBase64(tabName: string, b64: string) {
        this.openNewPdfTabFromBlob(
            tabName,
            PrintSheetUtils.base64ToBlob(b64, 'application/pdf'),
        );
    }

    // Throws rather than silently doing nothing when the browser blocks the
    // pop-up. Callers report success on the strength of this call, and one of
    // them goes on to record the document as delivered — which must never
    // happen for a PDF the user never received.
    private openNewPdfTabFromBlob(tabName: string, blob: Blob) {
        const pdfWindow = window.open('');
        if (!pdfWindow) {
            throw new Error(
                'The PDF could not be opened — allow pop-ups for this site, then print again.',
            );
        }
        pdfWindow.document.write(`<title>${tabName}</title>`);
        pdfWindow.document.write(
            `<style type='text/css'> body, html {margin: 0; padding: 0; height: 100%; overflow: hidden;}</style>`,
        );
        // Created from the POPUP's window, not this one. Chrome partitions
        // blob URLs by top-level site: when the console runs embedded in the
        // lnwshop admin (the Load Lnwshop Data context), a URL minted here
        // lives in the (lnwshop × console) partition and the popup — a plain
        // console tab — is refused it ("It may have been moved, edited, or
        // deleted"). Minting it in the popup keeps creator and consumer in
        // the same partition, and ties the URL's lifetime to the tab.
        const url = (
            pdfWindow as Window & typeof globalThis
        ).URL.createObjectURL(blob);
        pdfWindow.document.write(
            `<iframe style='border: 0px' width='100%' height='100%' frameborder'0' src='${url}'></iframe>`,
        );
    }
}
